import React, { useState } from 'react';
import PropTypes from 'prop-types';
import hive from '@bombas/hive';
import { productAttributePropType } from '../../Product.propTypes';
import ProductCallouts from '../ProductCallouts/ProductCallouts';
import Tooltip from '../../../Tooltip/Tooltip';
import * as Styled from './ProductFullDetailsBody.styled';

const replaceQuestionMark = (str) => (str === '?' ? '' : str);

/**
 * `ProductFullDetailsBody` is used to display a product's description, materials, care instructions, and tech.
 */
const ProductFullDetailsBody = ({
  callouts,
  care,
  className,
  composition,
  description_html,
  features,
  imported,
  title
}) => {
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [tooltipData, setTooltipData] = useState(null);

  return (
    <Styled.Container
      data-testid="productFullDetailsBody"
      className={className}
    >
      {description_html.length > 0 && (
        <Styled.DetailsBlock>
          <Styled.SectionTitle>
            {replaceQuestionMark(title)}
          </Styled.SectionTitle>
          <Styled.Description description={description_html} />
        </Styled.DetailsBlock>
      )}
      {callouts?.length > 0 && (
        <Styled.DetailsBlock>
          <Styled.SectionTitle>Product Highlight{callouts.length > 1 ? 's' : ''}</Styled.SectionTitle>
          <ProductCallouts callouts={callouts} />
        </Styled.DetailsBlock>
      )}
      {features.length > 0 && (
        <Styled.DetailsBlock>
          <Styled.SectionTitle>Features</Styled.SectionTitle>
          <ul>
            {features.map((t, i) => (
              <div key={i}>
                <Styled.Feature
                  onClick={(e) => {
                    setTooltipData(t);
                    setTooltipAnchor(e.currentTarget.lastChild);
                    hive.track('FPD_FEATURES_LIST_CLICK');
                  }}
                >
                  <img
                    style={{
                      width: '25px',
                      height: '25px',
                      marginRight: '8px',
                      marginLeft: '-4px'
                    }}
                    src={t.image.file.url}
                    alt={t.title}
                  />
                  <span>{t.title}</span>
                </Styled.Feature>
              </div>
            ))}
          </ul>
          <Tooltip
            anchorElement={tooltipAnchor}
            onClose={() => setTooltipAnchor(null)}
            title={tooltipData?.title}
            placement="top"
          >
            <Styled.TooltipContent>
              {replaceQuestionMark(tooltipData?.description)}
            </Styled.TooltipContent>
          </Tooltip>
        </Styled.DetailsBlock>
      )}
      {composition.length > 0 && (
        <Styled.DetailsBlock>
          <Styled.SectionTitle>Full Fabric Content</Styled.SectionTitle>
          {composition.map((materials, i) => (
            <div key={i}>
              <Styled.Materials>{materials.toLowerCase()}</Styled.Materials>
            </div>
          ))}
          {imported && <Styled.Imported>Imported</Styled.Imported>}
        </Styled.DetailsBlock>
      )}
      {replaceQuestionMark(care).length > 0 && (
        <Styled.DetailsBlock>
          <Styled.SectionTitle>Care Details</Styled.SectionTitle>
          <Styled.CareInstructions>
            {replaceQuestionMark(care)}
          </Styled.CareInstructions>
        </Styled.DetailsBlock>
      )}
    </Styled.Container>
  );
};

ProductFullDetailsBody.propTypes = {
  /**
   * Attribute value drivers to emphasize certain aspects of the product
   */
  callouts: PropTypes.arrayOf(PropTypes.shape(productAttributePropType)),
  /**
   * Product care instructions
   */
  care: PropTypes.string.isRequired,
  /**
   * CSS Class Name
   */
  className: PropTypes.string,
  /**
   * List of product materials
   */
  composition: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * Product description
   */
  description_html: PropTypes.string.isRequired,
  /**
   * Product features / technology
   */
  features: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string.isRequired
        })
      })
    })
  ).isRequired,
  /**
   *  `true` is the product is imported
   */
  imported: PropTypes.bool.isRequired,
  /**
   * Product title
   */
  title: PropTypes.string.isRequired
};

ProductFullDetailsBody.defaultProps = {
  callouts: [],
  className: null
};

export default ProductFullDetailsBody;
